import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { motion, AnimatePresence } from "framer-motion";
import { deleteDemoVideo, getAdminDemoVideo } from "redux/features/Admin/homeVideo/homeVideoSlice";
import DeleteModal from "components/DeleteModal/DeleteModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import { FiEye, FiEdit, FiTrash2, FiVideo, FiX } from "react-icons/fi";
import Loading from "components/Loader/Loading";
import { RiVideoLine } from "react-icons/ri";

export interface ListDemoVideosHomePageProps {
  className?: string;
}

const ListDemoVideosHomePage: FC<ListDemoVideosHomePageProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const videos: any = useAppSelector((state) => state?.videoHome?.allVideos);
  const loading = useAppSelector((state) => state?.videoHome?.loading);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);

  useEffect(() => {
    dispatch(getAdminDemoVideo());
  }, [dispatch]);

  const handleDeleteModalOpen = (video: any) => {
    setSelectedVideo(video);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedVideo && selectedVideo?.id !== undefined) {
      await dispatch(deleteDemoVideo(selectedVideo?.id));
      navigate(0);
      setDeleteModalOpen(false);
      await dispatch(getAdminDemoVideo());
    }
  };

  const handlePreviewOpen = (video: any) => {
    setSelectedVideo(video);
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div
      className={`nc-AccountPage ${className} bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-800 min-h-screen`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-12 mx-auto">
          <motion.div
            className="flex items-center justify-between mb-10"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center space-x-3">
              <RiVideoLine className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Home Demo Video</h2>
            </div>
            {videos.length === 0 && (
              <ButtonPrimary
                href="/admin/dashboard/settings/ajouter/home-demo-video"
                className="transition-transform duration-300 transform btn-primary-gradient hover:scale-105 sm:w-auto"
              >
                Ajouer video
              </ButtonPrimary>
            )}
          </motion.div>

          {deleteModalOpen && (
            <DeleteModal
              open={deleteModalOpen}
              handleClose={() => setDeleteModalOpen(false)}
              handleDelete={handleDeleteConfirm}
              category={{
                id: selectedVideo?.id || 0,
                designation: selectedVideo?.title || "",
              }}
            />
          )}

          {previewModalOpen && selectedVideo && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="relative w-full max-w-4xl p-6 mx-4 bg-white dark:bg-neutral-800 rounded-2xl">
                <button
                  onClick={handlePreviewClose}
                  className="absolute p-2 rounded-full top-4 right-4 hover:bg-gray-100 dark:hover:bg-neutral-700"
                >
                  <FiX className="text-2xl text-gray-600 dark:text-gray-300" />
                </button>
                <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">{selectedVideo.title}</h3>
                <video
                  className="w-full h-auto rounded-lg"
                  src={process.env.REACT_APP_CLOUD_FRONT + selectedVideo.url}
                  controls
                  autoPlay
                />
              </div>
            </motion.div>
          )}

          {loading === "pending" && <Loading />}

          {loading === "succeeded" && (!videos || videos.length === 0) && (
            <motion.div
              className="py-12 text-center bg-white shadow-lg dark:bg-neutral-800 rounded-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <FiVideo className="mx-auto mb-4 text-6xl text-gray-400 dark:text-gray-500" />
              <p className="text-xl text-gray-600 dark:text-gray-400">
                No videos available. Start by adding a new video!
              </p>
            </motion.div>
          )}

          {videos.length !== 0 ? (
            <motion.div
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, staggerChildren: 0.1 }}
            >
              <AnimatePresence>
                {videos && (
                  <motion.div
                    key={videos.id}
                    className="relative overflow-hidden transition-all duration-300 bg-white shadow-lg dark:bg-neutral-800 rounded-2xl hover:shadow-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.4 }}
                  >
                    {/* Video Preview */}
                    <div className="relative h-48 overflow-hidden bg-gray-200 dark:bg-neutral-700 group">
                      <video
                        className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                        src={process.env.REACT_APP_CLOUD_FRONT + videos.url}
                        muted
                        onMouseOver={(e) => e.currentTarget.play()}
                        onMouseOut={(e) => e.currentTarget.pause()}
                      />
                      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-black opacity-0 bg-opacity-20 group-hover:opacity-100">
                        <button
                          onClick={() => handlePreviewOpen(videos)}
                          className="p-3 transition-colors bg-white rounded-full shadow-lg dark:bg-neutral-900 hover:bg-primary-600 hover:text-white"
                        >
                          <FiEye className="text-xl" />
                        </button>
                      </div>
                    </div>

                    {/* Video Info */}
                    <div className="p-5">
                      <div className="flex items-center mb-3 space-x-2">
                        <FiVideo className="text-xl text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {videos.title}
                        </h3>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex space-x-3">
                          <Link
                            to={`/admin/dashboard/settings/edit/home-demo-video/${videos.id}`}
                            className="p-2 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700"
                            title="Edit Video"
                          >
                            <FiEdit className="text-lg text-blue-500" />
                          </Link>
                          <button
                            onClick={() => handleDeleteModalOpen(videos)}
                            className="p-2 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700"
                            title="Delete Video"
                          >
                            <FiTrash2 className="text-lg text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <>
              <div className="flex items-center justify-center h-screen">
                <div className="flex flex-col items-center justify-center">
                  <div>
                    <FiVideo className="mx-auto mb-4 text-6xl text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <p className="text-xl text-center text-gray-600 dark:text-gray-400">
                      Aucune vidéo disponible. Commencez par en ajouter une nouvelle !
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListDemoVideosHomePage;
